// Форматиране на количества по МЕРНА ЕДИНИЦА на продукта.
//
// Всяка единица носи `decimal_places` (units_of_measure.decimal_places, 0–4):
// колко знака след десетичната запетая да се показват за количества в нея —
// 0 → цяло число („1 бр", не „1,000"), 1 → един знак („1,5 л"), 3 → „8,000 кг".
// Заменя старите per-файлови `fmtQty`/`fmtQty3`, които бяха хардкоднати на 3.
//
// Количествата остават със ЗАПЕТАЯ (bg-BG) — за разлика от паричните суми, които
// са с точка. Мерната единица (`decimalPlaces`) идва по продукт от `useProducts`.
import { useMemo } from "react";

import { useProducts } from "../../../hooks/useInventory";

// По подразбиране (единица без/с неизвестни знаци, или продукт извън списъка).
export const DEFAULT_QTY_DECIMALS = 3;

export function clampDecimals(d?: number | null): number {
  if (d == null || Number.isNaN(d)) return DEFAULT_QTY_DECIMALS;
  return Math.min(4, Math.max(0, Math.trunc(d)));
}

// Количество с ТОЧНО `decimals` знака (запетая разделител, интервал за хилядите):
// decimals 0 → „1", 1 → „1,5", 3 → „8,000".
export function formatQty(value: number, decimals?: number | null): string {
  const d = clampDecimals(decimals);
  return Number(value ?? 0).toLocaleString("bg-BG", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
}

// Количеството, разделено на цяла част и дробна (със запетаята): „1 250" + „,500";
// при 0 знака дробната е празна. За колони, подравнени по десетичния знак.
export function splitQty(value: number, decimals?: number | null): { integer: string; fraction: string } {
  const d = clampDecimals(decimals);
  const parts = new Intl.NumberFormat("bg-BG", {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).formatToParts(Number(value ?? 0));
  const isFraction = (type: string) => type === "decimal" || type === "fraction";
  return {
    integer: parts.filter((part) => !isFraction(part.type)).map((part) => part.value).join(""),
    fraction: parts.filter((part) => isFraction(part.type)).map((part) => part.value).join(""),
  };
}

// Hook: връща getter `decimalsOf(productId)` → знаците за единицата на продукта
// (от `useProducts`), с fallback DEFAULT_QTY_DECIMALS за непознат продукт.
export function useUnitDecimals(): (productId?: number | null) => number {
  const { data: products } = useProducts();
  return useMemo(() => {
    const map = new Map<number, number>();
    for (const p of products ?? []) {
      if (p.id != null) map.set(p.id, clampDecimals(p.decimalPlaces));
    }
    return (productId?: number | null): number =>
      productId != null && map.has(productId)
        ? (map.get(productId) as number)
        : DEFAULT_QTY_DECIMALS;
  }, [products]);
}
